/**
 * DataframeBody — wrapper thuần trình bày cho VÙNG BẢNG của list page.
 *
 * Gom pattern "Reload đang tải" dùng chung (chị Đẹp chốt 2026-07): khi refetch
 * (`fetching && !loading`) → thanh <LinearProgress> 2px phía trên + bảng mờ
 * (opacity 0.55) + khóa tương tác cho tới khi API trả data mới. GIỮ data cũ
 * hiển thị (không blank), mirror PnLCard / ExposureLimitView.
 *
 * Trước đây mỗi list page tự viết `<Box sx={{ flex:1, minHeight:320,
 * overflow:'hidden' }}>` bọc <TableVirtuoso> và chỉ có 1 spinner 15px cạnh
 * pagination → nhìn như Reload không tác động. Thay Box đó bằng component này:
 *
 *   <DataframeBody loading={isLoading} fetching={isFetching}>
 *     {rows.length === 0 ? <BrandState .../> : <TableVirtuoso .../>}
 *   </DataframeBody>
 *
 * LƯU Ý: đây chỉ là lớp trình bày — KHÔNG đổi rule dữ liệu/cột/filter. Với các
 * trang trước đây blank cả bảng khi `isFetching` (guard `isLoading || isFetching`),
 * đổi guard về chỉ `isLoading` để bảng giữ data cũ rồi để component này làm mờ.
 */
import { Box, LinearProgress } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ReactNode } from 'react'

export interface DataframeBodyProps {
  /** Lần load đầu (chưa có data) — vùng bảng tự render loading state riêng, KHÔNG dim. */
  loading?: boolean
  /** Refetch/Reload khi đã có data — dim + strip. */
  fetching?: boolean
  /** minHeight của vùng bảng (default 320, khớp structure inline hiện có). */
  minHeight?: number
  /** sx bổ sung / override cho Box bảng (giữ tương thích trang có sx custom). */
  sx?: SxProps<Theme>
  children: ReactNode
}

export default function DataframeBody({
  loading,
  fetching,
  minHeight = 320,
  sx,
  children,
}: DataframeBodyProps) {
  const dim = Boolean(fetching) && !loading
  return (
    <>
      <Box sx={{ height: 2 }}>
        {dim && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight,
          overflow: 'hidden',
          opacity: dim ? 0.55 : 1,
          pointerEvents: dim ? 'none' : 'auto',
          transition: 'opacity 0.15s',
          ...sx,
        }}
      >
        {children}
      </Box>
    </>
  )
}
